import axios from "axios"
import { supabase } from "../supabaseClient"

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/voice` : "/api/voice",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      config.headers["user-id"] = session.user.id
    }

    // Check if admin has selected a company
    try {
      const selectedCompanyName = localStorage.getItem("selectedCompanyName")
      if (selectedCompanyName) {
        config.headers["x-company-name"] = selectedCompanyName
      }
    } catch (e) {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface AgentWeight {
  agent_id: string
  weight: number
  agent_version?: number | null
}

export interface PhoneNumber {
  phone_number: string
  phone_number_pretty: string
  inbound_agents?: AgentWeight[] | null
  outbound_agents?: AgentWeight[] | null
  area_code: number
  nickname: string | null
  inbound_webhook_url: string | null
  last_modification_timestamp: number
}

export interface CreatePhoneNumberParams {
  inbound_agents?: AgentWeight[] | null
  outbound_agents?: AgentWeight[] | null
  area_code?: number
  nickname?: string
  inbound_webhook_url?: string | null
  userId?: string | null // Add userId here
  company_name?: string // Add company_name for admin override
}

export interface UpdatePhoneNumberParams {
  inbound_agents?: AgentWeight[] | null
  outbound_agents?: AgentWeight[] | null
  nickname?: string | null
  inbound_webhook_url?: string | null
}

// Add this interface after the other interfaces
export interface ImportPhoneNumberParams {
  phone_number: string
  termination_uri: string
  sip_trunk_auth_username?: string
  sip_trunk_auth_password?: string
  inbound_agents?: AgentWeight[] | null
  outbound_agents?: AgentWeight[] | null
  nickname?: string
  inbound_webhook_url?: string | null
  company_name?: string // Add company_name for admin override
}

export const phoneNumberService = {
  list: async (companyName?: string) => {
    const url = companyName ? `/phone-numbers?company_name=${encodeURIComponent(companyName)}` : "/phone-numbers"
    const response = await api.get<PhoneNumber[]>(url)
    return response.data
  },

  get: async (phoneNumber: string) => {
    const response = await api.get<PhoneNumber>(`/phone-numbers/${phoneNumber}`)
    return response.data
  },

  create: async (params: CreatePhoneNumberParams) => {
    const response = await api.post<PhoneNumber>("/phone-numbers", params)
    return response.data
  },

  update: async (phoneNumber: string, params: UpdatePhoneNumberParams) => {
    const response = await api.patch<PhoneNumber>(`/phone-numbers/${phoneNumber}`, params)
    return response.data
  },

  delete: async (phoneNumber: string) => {
    await api.delete(`/phone-numbers/${phoneNumber}`)
  },

  // Add this method to the phoneNumberService object
  importPhoneNumber: async (params: ImportPhoneNumberParams) => {
    const response = await api.post<PhoneNumber>("/import-phone-number", params)
    return response.data
  },
}
